import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { googleCalendar } from '../services/GoogleCalendar';
import toast from 'react-hot-toast';
import { TimerSettings, defaultSettings } from '../types/timer';

// Simple debounce function
function debounce<T extends (...args: any[]) => any>(func: T, wait: number): T & { cancel: () => void } {
  let timeout: NodeJS.Timeout | null = null;
  
  const debounced = ((...args: any[]) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  }) as T & { cancel: () => void };
  
  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };
  
  return debounced;
}

interface TimerState {
  phase: 'work' | 'shortBreak' | 'longBreak';
  isActive: boolean;
  timeRemaining: number;
  completedPomodoros: number;
  currentSession: number;
  settings: TimerSettings;
}

interface TimerContextType {
  state: TimerState;
  start: () => void;
  pause: () => void;
  reset: () => void;
  skip: () => void;
  setPhase: (phase: 'work' | 'shortBreak' | 'longBreak') => void;
  updateSettings: (settings: TimerState['settings']) => void;
  startTimer: (duration: number, phase: TimerState['phase']) => void;
  updateDurations: (durations: { work: number; shortBreak: number; longBreak: number }) => void;
  setCompletedPomodoros: (count: number) => void;
}

const initialState: TimerState = {
  phase: 'work',
  isActive: false,
  timeRemaining: defaultSettings.workDuration * 60,
  completedPomodoros: 0,
  currentSession: 0,
  settings: defaultSettings
};

export const TimerContext = createContext<TimerContextType | null>(null);

type TimerAction =
  | { type: 'START' }
  | { type: 'PAUSE' }
  | { type: 'RESET' }
  | { type: 'TICK' }
  | { type: 'SKIP' }
  | { type: 'SET_PHASE'; phase: TimerState['phase'] }
  | { type: 'START_TIMER'; duration: number; phase: TimerState['phase'] }
  | { type: 'UPDATE_DURATIONS'; durations: { work: number; shortBreak: number; longBreak: number } }
  | { type: 'UPDATE_SETTINGS'; settings: TimerState['settings'] }
  | { type: 'SET_COMPLETED_POMODOROS'; count: number }
  | { type: 'RESTORE'; timeRemaining: number; phase: TimerState['phase']; completedPomodoros: number };

function timerReducer(state: TimerState, action: TimerAction): TimerState {
  switch (action.type) {
    case 'START':
      return {
        ...state,
        isActive: true,
      };
      
    case 'PAUSE':
      return {
        ...state,
        isActive: false
      };
      
    case 'RESET':
      return {
        ...state,
        isActive: false,
        timeRemaining: state.phase === 'work' 
          ? state.settings.workDuration * 60
          : state.phase === 'shortBreak'
          ? state.settings.shortBreakDuration * 60
          : state.settings.longBreakDuration * 60
      };
      
    case 'SKIP':
      const nextPhase = state.phase === 'work' 
        ? (state.completedPomodoros + 1) % state.settings.longBreakInterval === 0 
          ? 'longBreak' 
          : 'shortBreak'
        : 'work';
      
      return {
        ...state,
        phase: nextPhase,
        isActive: false,
        timeRemaining: nextPhase === 'work'
          ? state.settings.workDuration * 60
          : nextPhase === 'shortBreak'
          ? state.settings.shortBreakDuration * 60
          : state.settings.longBreakDuration * 60,
        completedPomodoros: state.phase === 'work' 
          ? state.completedPomodoros + 1 
          : state.completedPomodoros
      };
      
    case 'SET_PHASE':
      const phaseDuration = action.phase === 'work'
        ? state.settings.workDuration * 60
        : action.phase === 'shortBreak'
        ? state.settings.shortBreakDuration * 60
        : state.settings.longBreakDuration * 60;

      return {
        ...state,
        phase: action.phase,
        isActive: false,
        timeRemaining: phaseDuration
      };

    case 'UPDATE_SETTINGS':
      return {
        ...state,
        settings: action.settings,
        timeRemaining: state.phase === 'work'
          ? action.settings.workDuration * 60
          : state.phase === 'shortBreak'
          ? action.settings.shortBreakDuration * 60
          : action.settings.longBreakDuration * 60
      };

    case 'SET_COMPLETED_POMODOROS':
      return {
        ...state,
        completedPomodoros: action.count
      };

    case 'TICK':
      if (state.timeRemaining <= 0) {
        return {
          ...state,
          isActive: false,
          timeRemaining: 0
        };
      }
      return {
        ...state,
        timeRemaining: state.timeRemaining - 1
      };

    case 'START_TIMER':
      return {
        ...state,
        phase: action.phase,
        timeRemaining: action.duration,
        isActive: false
      };

    case 'RESTORE':
      return {
        ...state,
        phase: action.phase,
        timeRemaining: action.timeRemaining,
        completedPomodoros: action.completedPomodoros,
        isActive: false,
      };

    case 'UPDATE_DURATIONS':
      const currentPhaseDuration = state.phase === 'work'
        ? action.durations.work * 60
        : state.phase === 'shortBreak'
        ? action.durations.shortBreak * 60
        : action.durations.longBreak * 60;

      return {
        ...state,
        settings: {
          ...state.settings,
          workDuration: action.durations.work,
          shortBreakDuration: action.durations.shortBreak,
          longBreakDuration: action.durations.longBreak
        },
        timeRemaining: currentPhaseDuration
      };

    default:
      return state;
  }
}

const TIMER_STORAGE_KEY = 'pomomax_timer_state';

interface PersistedTimerState {
  phase: TimerState['phase'];
  timeRemaining: number;
  completedPomodoros: number;
  isActive: boolean;
  savedAt: number; // timestamp when state was saved
}

const saveTimerState = (state: TimerState) => {
  const persisted: PersistedTimerState = {
    phase: state.phase,
    timeRemaining: state.timeRemaining,
    completedPomodoros: state.completedPomodoros,
    isActive: state.isActive,
    savedAt: Date.now(),
  };
  localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify(persisted));
};

const loadTimerState = (settings: TimerSettings): TimerState | null => {
  try {
    const saved = localStorage.getItem(TIMER_STORAGE_KEY);
    if (!saved) return null;

    const persisted: PersistedTimerState = JSON.parse(saved);

    // If the timer was active when the browser closed, subtract elapsed time
    if (persisted.isActive && persisted.savedAt) {
      const elapsedSeconds = Math.floor((Date.now() - persisted.savedAt) / 1000);
      const adjustedTime = persisted.timeRemaining - elapsedSeconds;

      if (adjustedTime <= 0) {
        // Timer would have completed — don't restore, clear it
        localStorage.removeItem(TIMER_STORAGE_KEY);
        return null;
      }

      return {
        phase: persisted.phase,
        isActive: false, // Start paused so user can see where they left off
        timeRemaining: adjustedTime,
        completedPomodoros: persisted.completedPomodoros,
        currentSession: 0,
        settings,
      };
    }

    // Timer was paused — restore exactly as it was
    if (persisted.timeRemaining > 0) {
      const fullDuration = persisted.phase === 'work'
        ? settings.workDuration * 60
        : persisted.phase === 'shortBreak'
        ? settings.shortBreakDuration * 60
        : settings.longBreakDuration * 60;

      // Only restore if time remaining differs from full duration (i.e., session was in progress)
      if (persisted.timeRemaining < fullDuration) {
        return {
          phase: persisted.phase,
          isActive: false,
          timeRemaining: persisted.timeRemaining,
          completedPomodoros: persisted.completedPomodoros,
          currentSession: 0,
          settings,
        };
      }
    }

    return null;
  } catch {
    return null;
  }
};

export const TimerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(timerReducer, {
    ...initialState,
    completedPomodoros: 0
  });

  // Persist timer state to localStorage on every change
  useEffect(() => {
    saveTimerState(state);
  }, [state.phase, state.isActive, state.timeRemaining, state.completedPomodoros]);

  // Restore timer state on mount
  useEffect(() => {
    const restored = loadTimerState(state.settings);
    if (restored) {
      dispatch({ type: 'SET_PHASE', phase: restored.phase });
      // We need a special action to restore exact time remaining
      dispatch({ type: 'RESTORE', timeRemaining: restored.timeRemaining, phase: restored.phase, completedPomodoros: restored.completedPomodoros });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // Save state before browser closes
  useEffect(() => {
    const handleBeforeUnload = () => saveTimerState(state);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state]);

  // Create a debounced toast function
  const debouncedToast = useCallback(
    debounce(() => {
      toast.success('Settings updated successfully');
    }, 500),  // 500ms delay
    [] // Empty dependencies array since we want this to be created once
  );

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (state.isActive && state.timeRemaining > 0) {
      interval = setInterval(() => {
        dispatch({ type: 'TICK' });
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [state.isActive]);

  const start = async () => {
    try {
      if (googleCalendar.isAuthorized()) {
        await googleCalendar.addPomodoroEvent(
          new Date(),
          state.settings.workDuration,
          'Pomodoro Session'
        );
      }
    } catch (error) {
      console.error('Error adding calendar event:', error);
    }
    dispatch({ type: 'START' });
  };

  const pause = () => dispatch({ type: 'PAUSE' });
  const reset = () => dispatch({ type: 'RESET' });
  const skip = () => dispatch({ type: 'SKIP' });
  const setPhase = (phase: TimerState['phase']) => dispatch({ type: 'SET_PHASE', phase });
  
  const startTimer = (duration: number, phase: TimerState['phase']) => {
    dispatch({ type: 'START_TIMER', duration: duration * 60, phase });
  };

  const updateDurations = (durations: { work: number; shortBreak: number; longBreak: number }) => {
    dispatch({ type: 'UPDATE_DURATIONS', durations });
  };

  const updateSettings = (settings: TimerState['settings']) => {
    dispatch({ type: 'UPDATE_SETTINGS', settings });
    debouncedToast();
  };

  const setCompletedPomodoros = (count: number) => {
    dispatch({ type: 'SET_COMPLETED_POMODOROS', count });
  };

  // Cleanup the debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedToast.cancel();
    };
  }, [debouncedToast]);

  return (
    <TimerContext.Provider value={{
      state,
      start,
      pause,
      reset,
      skip,
      setPhase,
      updateSettings,
      startTimer,
      updateDurations,
      setCompletedPomodoros
    }}>
      {children}
    </TimerContext.Provider>
  );
};

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
};